import { useState } from 'react';
import { Alert, Button, Dialog, TextField } from '@dru/ui';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';
import { localDay } from './ArrangeDialog';

export interface CandidateDecisionTarget {
  id: string;
  fullName: string;
  version: number;
}

export function CandidateDecisionDialog({
  employee,
  decision,
  onClose,
  onDone,
}: {
  employee: CandidateDecisionTarget;
  decision: 'HIRE' | 'NOT_HIRED';
  onClose: () => void;
  onDone: () => void;
}) {
  const hiring = decision === 'HIRE';
  const [date, setDate] = useState(localDay);
  const [reason, setReason] = useState(hiring ? 'Candidate hired' : 'Recruitment process closed');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post(
        `/employees/${employee.id}/${hiring ? 'hire' : 'not-hired'}`,
        hiring
          ? { joinedOn: date, reason, version: employee.version }
          : { decidedOn: date, reason, version: employee.version },
      );
      onDone();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog title={`${hiring ? 'Mark hired' : 'Close candidate'} — ${employee.fullName}`} onClose={onClose}>
      <div className="space-y-4">
        {error && <Alert tone="danger">{error}</Alert>}
        <TextField
          label={hiring ? 'Joining date' : 'Decision date'}
          type="date"
          value={date}
          onChange={setDate}
          required
        />
        <TextField
          label="Reason"
          value={reason}
          onChange={setReason}
          required
          hint="At least three characters."
          info="Kept in the employment history and the audit trail."
        />
        <Alert tone={hiring ? 'info' : 'warning'}>
          {hiring
            ? 'The same medical record becomes an employee record and starts counting in workforce compliance.'
            : 'Their medical cases and reports are kept, but no new medical case can be arranged for them. This cannot be undone here.'}
        </Alert>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={hiring ? 'primary' : 'danger'}
            loading={busy}
            disabled={reason.trim().length < 3 || !date}
            onClick={submit}
          >
            {hiring ? 'Mark hired' : 'Close candidate'}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
